import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { getAuth, submitFeedback } from "../api";

const FEEDBACK_CATEGORIES = [
  "Bug Report",
  "Pricing Issue",
  "Feature Request",
  "General Feedback",
];

const buildInitialForm = (user, pageUrl) => ({
  name: String(user?.name || user?.username || "").trim(),
  email: String(user?.email || "").trim(),
  category: "General Feedback",
  message: "",
  page_url: pageUrl || "",
});

export default function FeedbackModal({ open, onClose, pageUrl = "", onSuccess }) {
  const auth = useMemo(() => getAuth(), []);
  const user = auth?.user || null;
  const [form, setForm] = useState(() => buildInitialForm(user, pageUrl));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(buildInitialForm(user, pageUrl));
    setError("");
  }, [open, user, pageUrl]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const message = String(form.message || "").trim();
    const email = String(form.email || "").trim();
    if (!message || message.length < 5) {
      setError("Please enter a message with at least 5 characters.");
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await submitFeedback({
        name: form.name,
        email: form.email,
        category: form.category,
        message,
        page_url: pageUrl || form.page_url || "",
      });
      setForm(buildInitialForm(user, pageUrl));
      setSuccessOpen(true);
      onClose?.();
      onSuccess?.();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        "Could not submit feedback right now."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>Send Feedback</DialogTitle>
        <DialogContent dividers sx={{ display: "grid", gap: 2.25, pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Share a bug, pricing concern, or idea. Feedback goes straight into our review queue.
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}

          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
            <TextField
              label="Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              fullWidth
              autoComplete="name"
            />
            <TextField
              label="Email"
              name="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              autoComplete="email"
            />
          </Box>

          <FormControl fullWidth>
            <InputLabel id="feedback-category-label">Category</InputLabel>
            <Select
              labelId="feedback-category-label"
              label="Category"
              name="category"
              value={form.category}
              onChange={handleChange}
            >
              {FEEDBACK_CATEGORIES.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Message"
            name="message"
            value={form.message}
            onChange={handleChange}
            fullWidth
            required
            multiline
            minRows={5}
            inputProps={{ maxLength: 4000 }}
            helperText={`${String(form.message || "").length}/4000`}
          />

          <TextField
            label="Current Page"
            value={pageUrl}
            fullWidth
            disabled
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Sending..." : "Submit Feedback"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={successOpen}
        autoHideDuration={3500}
        onClose={() => setSuccessOpen(false)}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccessOpen(false)}
        >
          Feedback submitted. Thank you.
        </Alert>
      </Snackbar>
    </>
  );
}
